"use client";

import type { QuestKey } from "@/components/QuestsV2/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { openAuthorizeUrl } from "@/utils/linkedaccounts/linkedAccounts";
import { X_MEDIA_UPLOAD_STORAGE_KEY } from "@/utils/linkedaccounts/x";
import {
  getQuestRecurrenceString,
  isQuestCompletedForPeriod,
} from "@/utils/quest/questCompletionUtils";
import { MODAL_HEIGHT, MODAL_WIDTH, appConfig } from "@constants";
import { ImageIcon, Upload, X } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fileToBase64 } from "../LinkedAccountLogin";

const X_LOGO_URL = "/images/quests/x.svg";

type PostTweetXModalProps = {
  visible: boolean;
  onClose: () => void;
  showConfirmationModal: () => void;
  questDetails: TweetQuestProps;
  gameId: string;
  gameName: string;
  gameUid: string;
  tweetDetails: TweetProps;
  completionDetails: CompletionProps;
};

type TweetQuestProps = {
  questRuleId: string;
  questKey: QuestKey;
  questLink: string;
};

type TweetProps = {
  tweetText?: string;
  tweetUsername?: string;
  tweetRequiresImage?: boolean;
};

type CompletionProps = {
  completed?: boolean;
  latestCompletion?: string | Date;
  refreshPeriodHours?: number;
  validFrom?: string | Date;
};

export function PostTweetXModal({
  visible,
  onClose,
  showConfirmationModal,
  questDetails,
  gameId,
  gameName,
  gameUid,
  tweetDetails,
  completionDetails,
}: PostTweetXModalProps) {
  const { questRuleId, questKey, questLink } = questDetails;
  const { tweetText, tweetUsername, tweetRequiresImage } = tweetDetails;
  const { completed, latestCompletion, refreshPeriodHours, validFrom } =
    completionDetails;

  const [tweetCustomText, setTweetCustomText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [tweetPrompt, setTweetPrompt] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { trackV3 } = useAnalytics();

  useEffect(() => {
    setTweetPrompt(tweetText ?? "");
  }, [tweetText]);

  const questCompletedForPeriod = useMemo(() => {
    return isQuestCompletedForPeriod({
      completed: completed || false,
      latestCompletion: latestCompletion,
      refreshPeriodHours: refreshPeriodHours,
      validFrom,
    });
  }, [completed, latestCompletion, refreshPeriodHours, validFrom]);

  // Get recurrence string for display
  const recurrenceString = useMemo(() => {
    return getQuestRecurrenceString(refreshPeriodHours);
  }, [refreshPeriodHours]);

  const modalHeight = useMemo(() => {
    if (questCompletedForPeriod) {
      return "500px";
    }
    if (tweetRequiresImage) {
      return "700px";
    }
    return MODAL_HEIGHT;
  }, [questCompletedForPeriod, tweetRequiresImage]);

  const trackModalEvent = useCallback(
    (action: Action, extras: Record<string, unknown> = {}) => {
      trackV3({
        object: "Quest",
        action,
        extras: {
          ruleId: questRuleId,
          questKey: questKey,
          gameId: gameId,
          gameName: gameName,
          isLoggedIn: true,
          ...extras,
        },
      });
    },
    [trackV3, questRuleId, questKey, gameId, gameName],
  );

  useEffect(() => {
    if (visible) {
      trackModalEvent(Action.VIEWED, {
        section: "QuestModal",
        tweetUsername: tweetUsername,
      });
    }
  }, [visible, trackModalEvent, tweetUsername]);

  const handleClose = useCallback(() => {
    trackModalEvent(Action.PRESSED, {
      control: "Close",
      controlType: "Button",
      section: "QuestModal",
    });
    setError(null);
    setImageError(null);
    setSelectedImage(null);
    onClose();
  }, [onClose, trackModalEvent]);

  const handleImageSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setImageError("Image must be smaller than 5MB");
        return;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        setImageError(
          "Please select a valid image file (JPEG, PNG, GIF, WebP)",
        );
        return;
      }

      setSelectedImage(file);
      setImageError(null);
    },
    [],
  );

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImageError(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handlePostOnX = useCallback(async () => {
    // Don't allow posting if quest is already completed for the period
    if (questCompletedForPeriod) {
      return;
    }

    if (tweetCustomText.trim() === "") {
      setError("The post content cannot be empty.");
      return;
    }

    // Check if image is required but not attached
    if (tweetRequiresImage && !selectedImage) {
      setImageError("An image is required for this post.");
      return;
    }

    trackModalEvent(Action.PRESSED, {
      control: "PostOnX",
      controlType: "Button",
      section: "QuestModal",
    });

    setError(null);
    setImageError(null);
    setIsProcessing(true);

    try {
      if (selectedImage && tweetRequiresImage) {
        const base64Image = await fileToBase64(selectedImage);
        const imageData = {
          base64: base64Image,
          filename: selectedImage.name,
          type: selectedImage.type,
        };
        const key = `${X_MEDIA_UPLOAD_STORAGE_KEY}_${questRuleId}`;
        localStorage.setItem(key, JSON.stringify(imageData));
      }

      let tweetContent = tweetCustomText;
      if (tweetUsername && !tweetCustomText.includes(tweetUsername)) {
        tweetContent = `${tweetUsername} ${tweetCustomText}`;
      }

      localStorage.removeItem(questRuleId);

      showConfirmationModal();

      openAuthorizeUrl(
        questRuleId,
        questKey,
        questLink,
        gameName,
        gameId,
        false,
        tweetContent,
      );

      trackModalEvent(Action.SUCCEEDED, {
        control: "PostOnX",
        section: "QuestModal",
      });

      handleClose();
    } catch (error) {
      console.error("Failed to initiate post on X:", error);
      setError("Failed to initiate post on X. Please try again.");

      trackModalEvent(Action.FAILED, {
        control: "PostOnX",
        section: "QuestModal",
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [
    questCompletedForPeriod,
    tweetCustomText,
    tweetRequiresImage,
    selectedImage,
    showConfirmationModal,
    questRuleId,
    questKey,
    questLink,
    gameName,
    gameId,
    handleClose,
    trackModalEvent,
    tweetUsername,
  ]);

  if (questKey !== "x-post-tweet-validation") {
    return null;
  }

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-[9998] bg-black/50" />
        <DialogContent
          className="fixed left-1/2 top-1/2 z-[9999] flex flex-col items-center justify-between rounded-[20px] text-white shadow-xl -translate-x-1/2 -translate-y-1/2 p-0 border-0 [&>button]:hidden"
          style={{
            backgroundColor: "#131313",
            height: modalHeight,
            width: "calc(100dvw - 40px)",
            maxWidth: MODAL_WIDTH,
            backgroundImage: `url("${appConfig.ASSET_BASE_URL}/images/quests/follow-confirmation-background.svg")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top",
          }}
        >
          <DialogTitle className="sr-only">Post on X Quest Modal</DialogTitle>
          <DialogDescription className="sr-only">
            Modal for posting a tweet on X social media platform as part of a
            quest completion
          </DialogDescription>

          <div className="flex flex-col items-center w-full gap-1">
            <Image
              src={`${appConfig.ASSET_BASE_URL}${X_LOGO_URL}`}
              alt="X Logo"
              width={64}
              height={64}
              className="mt-24 mb-8"
            />

            <h2
              className="text-center mx-12 text-white font-suisse"
              style={{
                fontSize: "28px",
                lineHeight: "32px",
                fontWeight: 700,
              }}
            >
              Posting to {tweetUsername}
            </h2>
          </div>

          <div className="flex-1 overflow-hidden px-4 w-full h-full overflow-y-auto">
            <div className="w-full max-w-[350px] mx-auto">
              {questCompletedForPeriod ? (
                <div className="text-center text-gray-300 px-4 py-8">
                  <p>
                    You've already done this for {recurrenceString}! Check back
                    later to complete it again.
                  </p>
                </div>
              ) : (
                <>
                  {/* Tweet Prompt Display */}
                  {tweetPrompt && (
                    <div className="relative mb-2 p-3 bg-white/5 border border-white/10 rounded-lg min-h-[50px] max-h-[70px] overflow-y-auto">
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">
                        {tweetPrompt}
                      </p>
                    </div>
                  )}

                  {/* Tweet Text Area */}
                  <div className="relative mb-2">
                    <textarea
                      value={tweetCustomText}
                      onChange={(e) => {
                        setTweetCustomText(e.target.value);
                        if (error) setError(null);
                      }}
                      placeholder={"Add your content here"}
                      rows={4}
                      className="flex min-h-[96px] w-full rounded-md border border-white/20 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:border-white/40 resize-none"
                    />
                    {error && (
                      <p className="text-sm text-red-400 mt-2 text-center">
                        {error}
                      </p>
                    )}
                  </div>

                  {/* Image Upload Section */}
                  {tweetRequiresImage && (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                      />

                      {!selectedImage ? (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full p-2 border-2 border-dashed border-white/20 rounded-lg hover:border-white/40 transition-colors flex flex-col items-center gap-0.5 text-gray-400 hover:text-white"
                        >
                          <Upload className="h-5 w-5" />
                          <span className="text-xs">
                            Click to upload image - PNG, JPG, WebP (max 5MB)
                          </span>
                        </button>
                      ) : (
                        <div className="relative p-2 border border-white/20 rounded-lg">
                          <div className="flex items-center gap-3">
                            <ImageIcon className="h-6 w-6 text-green-400" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">
                                {selectedImage.name}
                              </p>
                              <p className="text-xs text-gray-400">
                                {(selectedImage.size / 1024 / 1024).toFixed(2)}{" "}
                                MB
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveImage}
                              className="p-1 rounded-full hover:bg-white/10 transition-colors"
                              aria-label="Remove image"
                            >
                              <X className="h-4 w-4 text-gray-400 hover:text-white" />
                            </button>
                          </div>
                        </div>
                      )}

                      {imageError && (
                        <p className="text-sm text-red-400 mt-2 text-center">
                          {imageError}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex flex-col w-full px-4 pb-4">
            {!questCompletedForPeriod ? (
              <>
                <Button
                  variant="default"
                  onClick={handlePostOnX}
                  disabled={isProcessing}
                  className="w-full h-12 my-2 font-roboto font-medium"
                  size="lg"
                >
                  {isProcessing ? "Posting..." : "Post on X"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  className="w-full h-12 font-roboto font-medium hover:bg-white/10 hover:text-white"
                  size="lg"
                >
                  Go back
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                onClick={handleClose}
                className="w-full h-12 font-roboto font-medium hover:bg-white/10 hover:text-white"
                size="lg"
              >
                Go back
              </Button>
            )}
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-lg bg-transparent hover:bg-white/10 transition-colors opacity-70 hover:opacity-100"
            aria-label="Close modal"
          >
            <X className="h-6 w-6 text-white" />
          </button>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
